import { useEffect, useState } from "react";
import { api, REFERRAL_CATEGORIES, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { X, Anchor, Compass, Sparkles, Heart, ArrowRight, Check, Baby } from "lucide-react";
import MetadataFooter from "@/components/MetadataFooter";

const IMMEDIATE_NEEDS = [
  "Food", "Shelter", "Transportation", "Childcare", "Medical care",
  "Mental health support", "Financial assistance", "Safety planning",
];

const EMOTIONAL_STATES = [
  "calm", "hopeful", "stressed", "overwhelmed", "grieving", "anxious", "neutral",
];

/**
 * Single-page edit form for an existing A.N.C.H.O.R. intake.
 * All 6 sections visible at once for quick scanning & updates.
 */
export default function IntakeEditDialog({ intakeId, onClose, onSaved }) {
  const [intake, setIntake] = useState(null);
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get(`/intakes/${intakeId}`)
      .then(({ data }) => {
        setIntake(data);
        setForm({
          intake_type: data.intake_type || "full",
          feels_safe: data.feels_safe ?? null,
          safety_concerns: data.safety_concerns || "",
          safety_notes: data.safety_notes || "",
          immediate_needs: data.immediate_needs || [],
          immediate_needs_notes: data.immediate_needs_notes || "",
          resources_needed: data.resources_needed || [],
          resource_notes: data.resource_notes || "",
          emotional_state: data.emotional_state || "",
          grounding_notes: data.grounding_notes || "",
          support_systems: data.support_systems || "",
          next_steps: data.next_steps || "",
          referrals_needed: data.referrals_needed || [],
          follow_up_preference: data.follow_up_preference || "",
          follow_up_date: data.follow_up_date || "",
          maternal_relevant: data.maternal_relevant || false,
          pregnancy_status: data.pregnancy_status || "",
          postpartum_weeks: data.postpartum_weeks ?? "",
          maternal_notes: data.maternal_notes || "",
        });
      })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [intakeId]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleArr = (k, v) =>
    setForm((f) => {
      const arr = f[k] || [];
      return { ...f, [k]: arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v] };
    });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = { ...form };
      payload.postpartum_weeks = payload.postpartum_weeks === "" ? null : Number(payload.postpartum_weeks);
      await api.patch(`/intakes/${intakeId}`, payload);
      toast.success("Intake updated");
      onSaved && onSaved();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#0F172A]/40">
      <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[92vh] overflow-y-auto" data-testid="intake-edit-dialog">
        <div className="sticky top-0 bg-white border-b border-[#E5E5E0] px-6 py-4 flex justify-between items-center z-10">
          <div>
            <h2 className="font-display text-xl font-semibold text-[#0F172A]">Edit A.N.C.H.O.R. Intake</h2>
            <p className="text-xs text-[#1E3A8A] mt-0.5">All sections at a glance — update what's needed and save.</p>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-[#F9F8F6] rounded-full" data-testid="intake-edit-close-btn">
            <X className="w-5 h-5" />
          </button>
        </div>

        {loading || !form ? (
          <div className="p-12 text-center text-[#1E3A8A]">Loading…</div>
        ) : (
          <form onSubmit={submit} className="p-6 space-y-6">
            {/* Type */}
            <div className="flex gap-2">
              {["quick", "full"].map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => update("intake_type", t)}
                  className={`flex-1 py-2 rounded-full text-sm font-medium capitalize ${
                    form.intake_type === t ? "bg-[#1E3A8A] text-white" : "bg-[#F9F8F6] text-[#334155]"
                  }`}
                >
                  {t} intake
                </button>
              ))}
            </div>

            <Section icon={Anchor} title="A — Acknowledge Safety">
              <div className="grid grid-cols-3 gap-2 mb-3">
                {[{ v: true, l: "Feels safe" }, { v: false, l: "Does not" }, { v: null, l: "Unsure" }].map((opt) => (
                  <button
                    key={String(opt.v)}
                    type="button"
                    onClick={() => update("feels_safe", opt.v)}
                    className={`py-2 rounded-xl border text-sm font-medium ${
                      form.feels_safe === opt.v ? "bg-[#1E3A8A] text-white border-[#1E3A8A]" : "border-[#E5E5E0] text-[#334155]"
                    }`}
                  >
                    {opt.l}
                  </button>
                ))}
              </div>
              <TextArea label="Safety concerns" value={form.safety_concerns} onChange={(v) => update("safety_concerns", v)} />
              <TextArea label="Staff notes" value={form.safety_notes} onChange={(v) => update("safety_notes", v)} />
            </Section>

            <Section icon={Compass} title="N — Navigate Immediate Needs">
              <ChipGroup options={IMMEDIATE_NEEDS.map((n) => ({ value: n, label: n }))} selected={form.immediate_needs} onToggle={(v) => toggleArr("immediate_needs", v)} accent="bg-[#1E3A8A]" />
              <TextArea label="Notes" value={form.immediate_needs_notes} onChange={(v) => update("immediate_needs_notes", v)} />
            </Section>

            <Section icon={Sparkles} title="C — Connect to Resources">
              <ChipGroup options={REFERRAL_CATEGORIES} selected={form.resources_needed} onToggle={(v) => toggleArr("resources_needed", v)} accent="bg-[#7E937D]" />
              <TextArea label="Resource notes" value={form.resource_notes} onChange={(v) => update("resource_notes", v)} />
            </Section>

            <Section icon={Heart} title="H — Help with Grounding">
              <ChipGroup
                options={EMOTIONAL_STATES.map((s) => ({ value: s, label: s }))}
                selected={form.emotional_state ? [form.emotional_state] : []}
                onToggle={(v) => update("emotional_state", form.emotional_state === v ? "" : v)}
                accent="bg-[#F59E0B]"
              />
              <TextArea label="Grounding notes" value={form.grounding_notes} onChange={(v) => update("grounding_notes", v)} />
              <TextArea label="Support systems" value={form.support_systems} onChange={(v) => update("support_systems", v)} />
            </Section>

            <Section icon={ArrowRight} title="O — Outline Next Steps">
              <TextArea label="Next steps" value={form.next_steps} onChange={(v) => update("next_steps", v)} />
              <div className="mt-3">
                <label className="block text-sm font-medium text-[#334155] mb-2">Referrals to start</label>
                <ChipGroup options={REFERRAL_CATEGORIES} selected={form.referrals_needed} onToggle={(v) => toggleArr("referrals_needed", v)} accent="bg-[#1E3A8A]" />
              </div>
            </Section>

            <Section icon={Baby} title="Maternal & Family (optional)">
              <label className="flex items-center gap-3 p-3 rounded-xl bg-[#F9F8F6] border border-[#E5E5E0] mb-3">
                <input
                  type="checkbox"
                  checked={form.maternal_relevant}
                  onChange={(e) => update("maternal_relevant", e.target.checked)}
                />
                <span className="text-sm text-[#334155]">Maternal or family wellness is relevant</span>
              </label>
              {form.maternal_relevant && (
                <>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
                    {["pregnant", "postpartum", "trying", "other"].map((s) => (
                      <button
                        type="button"
                        key={s}
                        onClick={() => update("pregnancy_status", s)}
                        className={`py-2 rounded-xl text-sm font-medium border capitalize ${
                          form.pregnancy_status === s ? "bg-[#7E937D] text-white border-[#7E937D]" : "border-[#E5E5E0] text-[#334155]"
                        }`}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                  {form.pregnancy_status === "postpartum" && (
                    <div className="mb-3">
                      <label className="block text-sm font-medium text-[#334155] mb-1">Weeks postpartum</label>
                      <input type="number" value={form.postpartum_weeks} onChange={(e) => update("postpartum_weeks", e.target.value)} className="w-full px-4 py-2.5 rounded-xl bg-white border border-[#E5E5E0]" />
                    </div>
                  )}
                  <TextArea label="Maternal notes" value={form.maternal_notes} onChange={(v) => update("maternal_notes", v)} />
                </>
              )}
            </Section>

            <Section icon={Heart} title="R — Reconnect">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
                {["phone", "text", "email", "home_visit"].map((c) => (
                  <button
                    type="button"
                    key={c}
                    onClick={() => update("follow_up_preference", c)}
                    className={`py-2 rounded-xl text-sm font-medium border capitalize ${
                      form.follow_up_preference === c ? "bg-[#1E3A8A] text-white border-[#1E3A8A]" : "border-[#E5E5E0] text-[#334155]"
                    }`}
                  >
                    {c.replace("_", " ")}
                  </button>
                ))}
              </div>
              <div>
                <label className="block text-sm font-medium text-[#334155] mb-1">Follow-up date</label>
                <input type="date" value={form.follow_up_date} onChange={(e) => update("follow_up_date", e.target.value)} className="w-full px-4 py-2.5 rounded-xl bg-white border border-[#E5E5E0]" />
              </div>
            </Section>

            <MetadataFooter entity={intake} className="pt-2" />

            <div className="flex gap-3 pt-2">
              <button type="button" onClick={onClose} className="flex-1 py-3 rounded-full border border-[#E5E5E0] text-[#334155] font-medium">Cancel</button>
              <button
                type="submit"
                disabled={submitting}
                data-testid="intake-edit-submit-btn"
                className="flex-1 py-3 rounded-full bg-[#1E3A8A] text-white font-medium hover:bg-[#172554] disabled:opacity-60 inline-flex items-center justify-center gap-2"
              >
                <Check className="w-4 h-4" />
                {submitting ? "Saving…" : "Save changes"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

const Section = ({ icon: Icon, title, children }) => (
  <div className="border border-[#E5E5E0] rounded-2xl p-4 bg-[#F9F8F6]/40">
    <div className="flex items-center gap-2 mb-3">
      <div className="w-7 h-7 rounded-lg bg-[#E8ECE7] text-[#1E3A8A] flex items-center justify-center">
        <Icon className="w-4 h-4" strokeWidth={1.75} />
      </div>
      <h3 className="font-display font-semibold text-[#0F172A] text-sm">{title}</h3>
    </div>
    <div className="space-y-3">{children}</div>
  </div>
);

const TextArea = ({ label, value, onChange }) => (
  <div>
    <label className="block text-xs font-medium text-[#334155] mb-1">{label}</label>
    <textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      rows={2}
      className="w-full px-3 py-2 rounded-xl bg-white border border-[#E5E5E0] text-sm focus:outline-none focus:ring-2 focus:ring-[#7E937D]"
    />
  </div>
);

const ChipGroup = ({ options, selected, onToggle, accent = "bg-[#1E3A8A]" }) => (
  <div className="flex flex-wrap gap-2">
    {options.map((opt) => {
      const isSel = selected.includes(opt.value);
      return (
        <button
          type="button"
          key={opt.value}
          onClick={() => onToggle(opt.value)}
          className={`px-3 py-1.5 rounded-full text-xs font-medium border capitalize ${
            isSel ? `${accent} text-white border-transparent` : "bg-white border-[#E5E5E0] text-[#334155]"
          }`}
        >
          {opt.label}
        </button>
      );
    })}
  </div>
);
